import type { ValidateRules } from 'cdui-js';
import dayts from '@china-difi/lib/date';
import i18n from '../../../../../../i18n';

/** 规范为 YYYY-MM-DD；无法解析则 '' */
export function normalizeYmd(raw: string | undefined | null): string {
  const s = (raw || '').trim();
  if (!s) return '';
  const iso = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (iso) {
    return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  }
  return '';
}

const ymdToMs = (ymd: string): number | null => {
  const n = normalizeYmd(ymd);
  if (!n) return null;
  const ts = dayts.timestamp(`${n}T00:00:00`);
  return ts == null || Number.isNaN(ts) ? null : ts;
};

/** 设备本地日历「今天」YYYY-MM-DD */
function todayYmdLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 本地日历加 N 个自然月；目标月无对应日则落到该月最后一天（如 1/31→2/28） */
function addCalendarMonthsYmd(ymd: string, months: number): string {
  const n = normalizeYmd(ymd);
  if (!n) return '';
  const [y, m, d] = n.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const day = dt.getDate();
  dt.setMonth(dt.getMonth() + months);
  if (dt.getDate() !== day) {
    dt.setDate(0);
  }
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/** 到期须严格晚于起始（两端均须为合法 YYYY-MM-DD） */
export function isIdEndAfterStart(start: string, end: string): boolean {
  const a = ymdToMs(start);
  const b = ymdToMs(end);
  if (a == null || b == null) return true;
  return b > a;
}

/** 到期不能早于手机当前日期（按本地日） */
export function isIdEndOnOrAfterToday(end: string): boolean {
  const b = ymdToMs(end);
  const t = ymdToMs(todayYmdLocal());
  if (b == null || t == null) return true;
  return b >= t;
}

/**
 * 剩余有效期须满 1 个自然月（主流开户门槛）：
 * 到期日 >= 今天+1 自然月；刚好满 1 个月可通过。
 */
export function isIdEndHasMinOneMonth(end: string): boolean {
  const b = ymdToMs(end);
  const min = ymdToMs(addCalendarMonthsYmd(todayYmdLocal(), 1));
  if (b == null || min == null) return true;
  return b >= min;
}

/**
 * 到期日错误文案。判断顺序（由严到具体，避免文案误导）：
 * 1) 已过期（早于今天）
 * 2) 剩余不足 1 个自然月
 * 3) 不晚于起始日期
 * 长期有效或空到期日返回 undefined。
 */
export function getIdCardEndDateError(
  start: string | undefined | null,
  end: string | undefined | null,
  longTerm: number,
): string | undefined {
  if (longTerm === 1) return undefined;
  const e = normalizeYmd(end);
  if (!e) return undefined;

  if (!isIdEndOnOrAfterToday(e)) {
    return i18n.OpenProgress.Info.UploadLic.ValidateEndNotBeforeToday;
  }
  if (!isIdEndHasMinOneMonth(e)) {
    return i18n.OpenProgress.Info.UploadLic.ValidateEndMinRemainingOneMonth;
  }
  const s = normalizeYmd(start);
  if (s && !isIdEndAfterStart(s, e)) {
    return i18n.OpenProgress.Info.UploadLic.ValidateEndAfterStart;
  }
  return undefined;
}

type IdCardDateFields = {
  idCardStartDate?: string;
  idCardEndDate?: string;
};

/** 写入后规范化；到期不满足任一规则则清空到期 */
export function syncIdCardDateRange(form: IdCardDateFields): void {
  const start = normalizeYmd(form.idCardStartDate);
  const end = normalizeYmd(form.idCardEndDate);
  if (start) form.idCardStartDate = start;
  if (end) form.idCardEndDate = end;
  if (end && getIdCardEndDateError(start, end, 0)) {
    form.idCardEndDate = '';
  }
}

export function createIdCardEndDateRules(
  getStartDate: () => string,
  getLongTerm: () => number,
): ValidateRules {
  return {
    idCardEndDate: {
      type: 'string',
      onvalidate(target) {
        return getIdCardEndDateError(getStartDate(), String(target.value || ''), getLongTerm());
      },
    },
  };
}
